import React, { useState } from 'react';
import { useAuditHistory } from '../hooks/useAuditHistory';
import { AuditTimeline } from './AuditTimeline';
import { AuditEntryDetail } from './AuditEntryDetail';
import type { AuditHistoryPanelProps } from './AuditHistoryPanel.types';

/**
 * Panel de historial de auditoría con split view (B+C):
 * - Panel izquierdo: AuditTimeline con cards y paginación
 * - Panel derecho: AuditEntryDetail con diff campo por campo
 *
 * Gestiona internamente el estado via useAuditHistory.
 * Mobile (<768px): muestra un panel a la vez con botón "Volver".
 */
export const AuditHistoryPanel: React.FC<AuditHistoryPanelProps> = ({
  fetchFn,
  entityId,
  pageSize = 20,
  className = '',
}) => {
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const { data, isLoading, isError, page, setPage, selectedEntryId, setSelectedEntryId, selectedEntry } =
    useAuditHistory({ fetchFn, entityId, pageSize });

  const handleSelect = (id: string) => {
    setSelectedEntryId(id);
    setMobileShowDetail(true);
  };

  const containerClasses = [
    'flex h-full max-h-[70vh] overflow-hidden',
    'bg-background-secondary dark:bg-dark-bg-primary',
    className,
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (isError) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="flex flex-col items-center justify-center h-48 gap-2 text-content-secondary dark:text-dark-content-secondary"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8 text-red-400"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
        <p className="text-sm">Error al cargar el historial. Intenta de nuevo.</p>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {/* Panel izquierdo — Timeline (oculto en mobile cuando se ve detalle) */}
      <div
        className={[
          'w-80 flex-shrink-0 border-r border-border-primary dark:border-dark-border-primary',
          mobileShowDetail ? 'hidden md:flex md:flex-col' : 'flex flex-col',
        ].join(' ')}
      >
        <AuditTimeline
          entries={data?.items ?? []}
          selectedId={selectedEntryId}
          onSelect={handleSelect}
          isLoading={isLoading}
          totalCount={data?.totalCount ?? 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          className="flex-1"
        />
      </div>

      {/* Panel derecho — Detail (oculto en mobile cuando se ve timeline) */}
      <div
        className={[
          'flex-1 flex flex-col overflow-hidden',
          !mobileShowDetail ? 'hidden md:flex' : 'flex',
        ].join(' ')}
      >
        {/* Mobile back button */}
        {mobileShowDetail && (
          <button
            className="flex items-center gap-1 px-4 py-2 text-sm text-primary-600 dark:text-primary-500 border-b border-border-primary dark:border-dark-border-primary md:hidden hover:bg-background-secondary dark:hover:bg-dark-bg-secondary"
            onClick={() => setMobileShowDetail(false)}
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            Volver al historial
          </button>
        )}

        <div className="flex-1 overflow-y-auto">
          <AuditEntryDetail entry={selectedEntry} />
        </div>
      </div>
    </div>
  );
};

AuditHistoryPanel.displayName = 'AuditHistoryPanel';
